import { useLayoutEffect, useRef, useState } from 'react';
import { nextSportsnewSessionOwner } from '../_helpers/sportsnewSessionOwner';
import { createSportsnewDetailRealtimeLedger, syncSportsnewCommandScope } from '../_helpers/sportsnewDetailRealtime';
import { sportsnewOddStateIdOf } from '../_helpers/sportsnewOddStateId';

export const useSportsnewSessionOwner = (identity) => {
  const [sessionOwner, setSessionOwner] = useState(() => nextSportsnewSessionOwner(null, identity));
  const next = nextSportsnewSessionOwner(sessionOwner, identity);
  // Conditional derived state retries this provider render before its children
  // commit; stable user/token renders preserve the same generation and socket.
  if (next !== sessionOwner) setSessionOwner(next);
  const sessionOwnerRef = useRef(sessionOwner);
  useLayoutEffect(() => { sessionOwnerRef.current = sessionOwner; }, [sessionOwner]);
  return { sessionOwner, sessionOwnerRef };
};

export const useSportsnewSessionBoundary = (sessionOwner, {
  selectedWsFreshnessRef, selectedRecoveryInFlightRef, membershipFailClosedFixturesRef,
  detailCacheRef, listLastGoodByKeyRef, fixtureRecoveryInFlightRef,
  detailRealtimeLedgerRef, modeRef, displayScopeRef, selectionsRef,
  cancelListFlights, clearFixtureDetail, commitFixtures, setResolvedListFilterKey,
}) => {
  useLayoutEffect(() => {
    const recoveryFlights = fixtureRecoveryInFlightRef.current;
    selectedWsFreshnessRef.current.clear();
    selectedRecoveryInFlightRef.current.clear();
    membershipFailClosedFixturesRef.current.clear();
    detailCacheRef.current.clear();
    listLastGoodByKeyRef.current.clear();
    cancelListFlights();
    // The account boundary must clear visible data before paint, not in a
    // deferred task that leaves the previous account's selections interactive.
    clearFixtureDetail();
    commitFixtures([]);
    setResolvedListFilterKey(null);
    detailRealtimeLedgerRef.current = createSportsnewDetailRealtimeLedger();
    syncSportsnewCommandScope(detailRealtimeLedgerRef.current, sessionOwner,
      modeRef.current, displayScopeRef.current,
      selectionsRef.current.map(sportsnewOddStateIdOf).filter(id => id != null));
    for (const flight of recoveryFlights.values()) flight.controller?.abort();
    recoveryFlights.clear();
    return () => {
      for (const flight of recoveryFlights.values()) flight.controller?.abort();
      recoveryFlights.clear();
    };
  }, [sessionOwner, selectedWsFreshnessRef, selectedRecoveryInFlightRef,
    membershipFailClosedFixturesRef, detailCacheRef, listLastGoodByKeyRef,
    fixtureRecoveryInFlightRef, detailRealtimeLedgerRef, modeRef, displayScopeRef,
    selectionsRef, cancelListFlights, clearFixtureDetail, commitFixtures, setResolvedListFilterKey]);
};
